import TagManager from 'react-gtm-module';

import { GA4_EVENT_NAMES } from './constants';
import {
  calculateDiscount,
  createCategoryPath,
  determineLowInventoryStatus,
  productDescriptionComingSoon,
} from './utils';
import {
  Breadcrumbs,
  MecProduct,
  ProductTag,
} from '@adapters/bigcommerce/types/product';

const { CURRENCY_CAD, ECOM_INTERACTION } = GA4_EVENT_NAMES;

type DataLayerProductData = {
  hasComparison?: boolean;
  hasMemberPriceDifference: boolean;
  code?: string;
  gaName: string;
  description?: string;
  brand?: { name?: string } | null;
  isCanadianBrand?: boolean;
  madeInCanada?: boolean;
  breadcrumbs?: Breadcrumbs[];
  selectedPrice?: {
    basePrice?: { value: number } | null;
    price?: { value: number } | null;
  };
  erpCategory?: string;
  itemListName?: string | null;
  selectedColour?: string | null;
  gender?: string | null;
  promoBadge?: { code: string | null; is_member_price?: boolean };
  variants?: MecProduct['variants'];
  selectedVariantId?: string;
  index?: number;
  showMemberPricingDealsBanner?: boolean;
  productTags?: ProductTag[];
  dealsBanner?: string[] | null;
} | null;

const createDataLayerProduct = (
  products: DataLayerProductData[],
  eventName: string
) => {
  return products.map((product) => {
    let noItemDescription;
    if (product?.description) {
      noItemDescription = productDescriptionComingSoon(product.description);
    }

    const category =
      product?.breadcrumbs && createCategoryPath(product.breadcrumbs);

    const { discount, discountTier } = calculateDiscount(
      product?.selectedPrice?.basePrice?.value ?? 0,
      product?.selectedPrice?.price?.value ?? 0
    );

    const availableVariants = product?.variants?.filter(
      (variant) => variant.availableForSaleOnline
    );
    const selectedVariant =
      availableVariants?.find(
        (variant) => variant.entityId === product?.selectedVariantId
      ) || (availableVariants?.length === 1 ? availableVariants[0] : undefined);

    return {
      item_id: product?.code ?? null,
      item_name: product?.gaName ?? null,
      discount: discount ?? null,
      discount_tier: discountTier ?? null,
      is_member_pricing:
        (product?.hasMemberPriceDifference ||
          product?.promoBadge?.is_member_price) ??
        null,
      item_brand: product?.brand?.name ?? null,
      ...(eventName === 'view_item' && {
        item_canadian_brand: product?.isCanadianBrand ?? false,
        item_canadian_made: product?.madeInCanada ?? false,
      }),
      item_category: (category || product?.erpCategory) ?? null,
      item_list_name: product?.itemListName ?? null,
      item_description_coming_soon: noItemDescription ?? null,
      item_colour: product?.selectedColour ?? null,
      item_gender: product?.gender ?? null,
      item_sticker: product?.promoBadge?.code?.toLowerCase() ?? null,
      compare_grid: product?.hasComparison ?? null,
      item_tags: product?.productTags?.map((tag) => tag?.name ?? null) ?? null,
      deals_banner: product?.dealsBanner ?? null,
      price:
        (product?.selectedPrice?.price?.value ||
          product?.selectedPrice?.basePrice?.value) ??
        null,
      item_variant: selectedVariant?.sku ?? null,
      index: product?.index ?? null,
      ...(!product?.itemListName // checking for item list name as we don't want to add this key if it's set (aka a used with a recommendation)
        ? {
            item_low_inventory_callout:
              determineLowInventoryStatus(selectedVariant),
          }
        : {}),
    };
  });
};

const trackProductEvent = ({
  eventName,
  products,
  itemListName,
}: {
  eventName: string;
  products: DataLayerProductData[];
  itemListName?: string | undefined;
}) => {
  const dataLayerProduct = createDataLayerProduct(products, eventName);
  TagManager.dataLayer({ dataLayer: { ecommerce: null } }); // Clear ecommerce object
  TagManager.dataLayer({
    dataLayer: {
      event: ECOM_INTERACTION,
      event_name: eventName,
      ecommerce: {
        value:
          dataLayerProduct.length === 1 ? dataLayerProduct?.[0]?.price : null,
        currency: CURRENCY_CAD,
        item_list_name: itemListName ?? null,
        items: dataLayerProduct,
      },
    },
  });
};

export default trackProductEvent;
